import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import TOCModal from "Pages/MySchedule/TOCModal/TOCModal";
import MarkedLectureViewModalAntD from "Pages/MySchedule/TOCModal/MarkedLectureViewModalAntD";
import { ToastContextDispatch, UserInfoContext } from "Context/CommonContext";
import { getAPIRequest } from "utilFunctions/Axios/AxiosRequestConfiguration";
import getRequestInterface from "utilFunctions/Axios/getRequestInterface";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { LectureData } from "./LectureTypes";
import { inBetweenTime } from "utilFunctions/shared/ScheduleStatus";
import useViewAccess from "Pages/MySchedule/Hooks/useViewAccess";
import { UserRoles } from "utilFunctions/Constants/UserRoles";

interface SyllabusTabProps {
  lecture?: LectureData;
}

const SyllabusTab: React.FC<SyllabusTabProps> = ({ lecture }) => {
  const ToastDispatch = useContext<any>(ToastContextDispatch);
  const userInfo = useContext<any>(UserInfoContext);
  const { screen } = useWindowSize();
  const isEditingMarkedLectureRef = useRef(false);
  const { isViewOnly } = useViewAccess({
    UserRoleAccess: [UserRoles.academicCounsellor],
  });
  const [planneLecturedList, setplanneLecturedList] = useState<any>([]);
  const [plannedLecture, setplannedLecture] = useState<any>(null);
  const [, setIspaneOpen] = useState(true);

  const [lecturePrecheckedNodes, setLecturePrecheckedNodes] = useState<any>([]);
  const [isLectureViewModal, setIsLectureViewModal] = useState(false);
  const [showTOCModal, setShowTOCModal] = useState(false);

  const allCoveredTopics: any[] = [];

  const toggleModal = () => {
    setIspaneOpen((prev) => !prev);
  };

  const modalCloseHandler = () => {
    setLecturePrecheckedNodes([]);
    toggleModal();
    if (isEditingMarkedLectureRef.current) {
      isEditingMarkedLectureRef.current = false;
      setIsLectureViewModal(true);
      setShowTOCModal(false);
    }
  };

  const getPlannedLecture = useCallback(
    async (url: string) => {
      await getAPIRequest({
        url,
        success: (response: any) => {
          if (response?.data?.data) {
            setplanneLecturedList(response.data.data.plannedLectureIdList);
            setplannedLecture(response.data.data);
          } else {
            setplanneLecturedList([]);
            setplannedLecture(null);
          }
        },
        failure: (error: any) => {
          setplanneLecturedList([]);
          setplannedLecture(null);

          ToastDispatch({
            isOpen: true,
            type: "Error",
            message:
              error?.response?.data?.code === "NF"
                ? "No Topic Present for this lecture"
                : error.message,
          });
        },
      } as getRequestInterface);
    },
    [ToastDispatch]
  );
  const selectedLecture = lecture;
  const editHandler = (lectureAssetsData: any) => {
    isEditingMarkedLectureRef.current = true;
    if (selectedLecture?.revisionRank && selectedLecture.revisionRank !== 0) {
      setLecturePrecheckedNodes(["revision_" + selectedLecture.revisionRank]);
    } else {
      const lectureAssetsDataIds = lectureAssetsData.map(
        (item: any) => item.nodeId
      );

      setLecturePrecheckedNodes(lectureAssetsDataIds);
    }

    setIsLectureViewModal(false);
    setShowTOCModal(true);
  };

  useEffect(() => {
    if (!lecture) return;

    if (!isEditingMarkedLectureRef.current) {
      if (lecture?.syllabusStatus === "Marked") {
        setIsLectureViewModal(true);
        setShowTOCModal(false);
      } else {
        setShowTOCModal(true);
        setIsLectureViewModal(false);
      }
    }
  }, [lecture]);

  const isShowLectureMark = (cardViewData: any) => {
    let showMarks = false;
    if (cardViewData.syllabusStatus == "Upcoming") {
      showMarks = false;
    } else if (cardViewData.syllabusStatus == "Marked") {
      showMarks = true;
    } else if (
      cardViewData.syllabusStatus == "Unmarked" ||
      inBetweenTime(cardViewData?.startTime, cardViewData?.endTime)
    ) {
      if (inBetweenTime(cardViewData?.startTime, cardViewData?.endTime)) {
        showMarks = false;
      } else {
        if (!isViewOnly && cardViewData?.markLecture) {
          showMarks = true;
        }
      }
    }
    return showMarks;
  };

  const isMobile = screen === "sm";
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        padding: isMobile ? "" : "10px 10px 80px 10px",
        height: "calc(100vh - 280px)",
        overflow: "auto",
      }}
    >
      <style>{`
        .lecture-toc-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .lecture-toc-wrapper button.btn-aeslPrimary,
        .markedLectureViewModalBody button.btn-aeslPrimary,
        .filterFooter button.btn-aeslPrimary,
        .filterFooter .footerAddCommentBtn {
          background-color: var(--primary-text) !important;
        }

        .lecture-toc-wrapper div:has(> .ant-switch) span,
        div:has(> .ant-switch) span {
          white-space: nowrap !important;
          display: inline-block !important;
        }  

        @media (min-width: 577px) {
          .lecture-toc-wrapper .pb-7 {
            padding-bottom: 0 !important;
          }

          .lecture-toc-wrapper div[style*="margin-top: -93px"],
          .lecture-toc-wrapper div[style*="space-between"] {
            position: fixed !important;
            bottom: 0 !important;
            left: 88px !important;
            right: 0 !important;
            width: 95% !important;
            margin: 0 !important;
            padding: 12px 24px !important;
            background-color: #ffffff !important;
            border-top: 1px solid #e5e7eb !important;
            box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08) !important;
            z-index: 999 !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            box-sizing: border-box !important;
            height: 60px !important;
          }
        }

        .lecture-toc-wrapper .ant-switch.ant-switch-checked {
          background-color: var(--primary-text) !important;
        }
        .lecture-toc-wrapper .ant-switch.ant-switch-checked:hover:not(.ant-switch-disabled) {
          background-color: var(--primary-text) !important;
        }
        .lecture-toc-wrapper div.listingDiv {
          background-color: #ffffff !important;
        }    

        ${
          isMobile
            ? `
              .lecture-toc-wrapper div[class*="footer"], 
              .lecture-toc-wrapper div[class*="Footer"],
              .lecture-toc-wrapper button[class*="mark"],
              .lecture-toc-wrapper [class*="submit"],
              .lecture-toc-wrapper .bottom-btn-container {
                bottom: 65px !important;
                left: 0px !important;
                right: 0px !important;
                height: 40px !important;
              }

              .lecture-toc-wrapper {
                margin-bottom: 50px;
              }
              .lecture-toc-wrapper .card {
                height: 100% !important;
              }  
              .kRYVec {
                height: 100% !important;
              }  
            `
            : ""
        }
      `}</style>

      {selectedLecture?.syllabusStatus === "Marked" && isLectureViewModal ? (
        <MarkedLectureViewModalAntD
          mode="myScheduled"
          inlineView={true}
          selectedlecture={selectedLecture}
          isLectureViewModal={isLectureViewModal}
          setIsLectureViewModal={setIsLectureViewModal}
          editHandler={editHandler}
        />
      ) : null}

      {showTOCModal && !isLectureViewModal && (
        <div
          className="lecture-toc-wrapper"
          style={{ position: "relative", padding: "10px 0px 0px 0px" }}
        >
          <TOCModal
            mode="myScheduleHome"
            selectedlecture={selectedLecture}
            planneLecturedList={planneLecturedList}
            allCoveredTopics={allCoveredTopics}
            lecturePrecheckedNodes={lecturePrecheckedNodes}
            setLecturePrecheckedNodes={setLecturePrecheckedNodes}
            plannedLecture={plannedLecture}
            toggleModal={toggleModal}
            userInfo={userInfo}
            getPlannedLecture={getPlannedLecture}
            screen={screen}
            setIspaneOpen={setIspaneOpen}
            modalCloseHandler={modalCloseHandler}
            header={<></>}
            ispaneopen={true}
            batchName={lecture?.batchName}
            subject={lecture?.subject}
            isNewUI={!isShowLectureMark(lecture)}
          />
        </div>
      )}
    </div>
  );
};

export default SyllabusTab;
